import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useState } from 'react';

import { PageHeader } from '@/components/common/page-header';
import { SectionCard } from '@/components/common/section-card';
import { AttachmentPreview } from '@/components/support/attachment-preview';
import { ConversationThread } from '@/components/support/conversation-thread';
import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { ErrorState } from '@/components/fleet/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBugBountyCampaign, useConfirmBugReport } from '@/hooks/use-bug-reports';
import { useBugReportDetail, useUpdateTicketStatus } from '@/hooks/use-bug-report-detail';
import { ApiError } from '@/lib/api-client';
import type { BugReportStatus } from '@/types/bug-reports';

const STATUS_OPTIONS: BugReportStatus[] = ['open', 'in_progress', 'resolved'];

// The diagnostics allowlist (support-diagnostics.ts) permits more than plain
// strings — orbitChunkIds is a string array — so a bare String(value) would
// collapse an array into an unreadable "a,b,c" with no list indication.
function formatDiagnosticValue(value: unknown): string {
  return Array.isArray(value) ? value.join(', ') : String(value);
}

// Friendly labels for the backend's diagnostics allowlist (support-diagnostics.ts,
// ALLOWED_DIAGNOSTIC_KEYS) — an operator reading "serverRegion" has to guess what it means;
// "Last connected server" doesn't. Falls back to the raw key for anything not listed here, so a
// new allowlisted key never silently disappears while waiting on a label to be added.
const DIAGNOSTIC_LABELS: Record<string, string> = {
  appVersion: 'App version',
  appBuild: 'App build',
  platform: 'Platform',
  locale: 'Locale',
  protocol: 'Protocol',
  serverRegion: 'Last connected server',
  lastConnectOutcome: 'Last connect outcome',
  orbitTurnCount: 'Orbit turn count',
  orbitChunkIds: 'Orbit chunk ids',
};

function diagnosticLabel(key: string): string {
  return DIAGNOSTIC_LABELS[key] ?? key;
}

// The wire payload keys structured answers "q1"/"q2"/... by position (BugCategory.questionResIds
// on Android/iOS — see PRD-0013 R1), never by the question text itself, so the server has no way
// to echo the real question back. Mirrors products/bug-report-conversation.md's R1 table
// (planet-proxy-KB) so an operator sees the actual question, not a "q1"/"q2" placeholder.
//
// Each category lists its CURRENT 2 questions plus, where one existed, the 3rd question that was
// dropped in the 2026-10-16 "3→2 questions per category" revision (Android/iOS BugCategory) — a
// ticket filed before that cutover can still carry a "q3" answer, and this table must keep
// resolving it rather than falling back to the raw key for exactly the field the cutover removed.
// Arrays (not a fixed-size tuple) so a future per-category question count change — more or fewer
// — only ever needs an edit here, never a change to structuredAnswerLabel's matching logic.
// Falls back to the raw key for an unknown category or an index beyond what it defines, rather
// than showing nothing.
const BUG_REPORT_QUESTIONS: Record<string, string[]> = {
  connection: [
    'Which server/location were you connecting to?',
    'What happened — failed to connect, connected with no internet, disconnected unexpectedly, or very slow to connect?',
    'Which protocol or mode were you using, if known?',
  ],
  speed: [
    'What kind of slowness — browsing, streaming, downloads, or the app itself?',
    'Which server were you connected to?',
    'Any speed-test numbers you can share? (optional)',
  ],
  crash: [
    'What were you doing right before it happened?',
    'Does it happen every time, sometimes, or did it happen once?',
    'Did you see an on-screen error message? (optional)',
  ],
  billing: [
    "What's the issue — payment, renewal, or missing access?",
    'Which store/payment method — Google Play, App Store, other?',
    'Do you have an order/transaction id? (optional)',
  ],
  ui: [
    'Which screen or feature is this about?',
    'What did you expect, and what happened instead?',
    'Is this every time, sometimes, or a one-time glitch?',
  ],
  other: [
    "Briefly, what's going on?",
    'When did you first notice it?',
    'Anything else we should know? (optional)',
  ],
};

function structuredAnswerLabel(category: string | null, wireKey: string): string {
  const match = /^q(\d+)$/.exec(wireKey);

  if (!match || !category) return wireKey;

  const questions = BUG_REPORT_QUESTIONS[category];

  return questions?.[Number(match[1]) - 1] ?? wireKey;
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{children}</span>
    </div>
  );
}

export function SupportBugReportDetailPage(): JSX.Element {
  const { reference } = useParams<{ reference: string }>();
  const [confirmGrantOpen, setConfirmGrantOpen] = useState(false);

  const detail = useBugReportDetail(reference);
  const campaign = useBugBountyCampaign();
  const confirmGrant = useConfirmBugReport();
  const updateStatus = useUpdateTicketStatus();

  if (detail.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (detail.isError || !detail.data) {
    return (
      <ErrorState
        title="Couldn't load this bug report."
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const report = detail.data;

  function handleGrant(): void {
    if (!reference) return;
    confirmGrant.mutate(reference, {
      onSuccess: (result) => {
        toast.success(`${result.rewardMonths} months of Premium granted to user ${result.userId}.`);
        setConfirmGrantOpen(false);
      },
      onError: (err) => {
        setConfirmGrantOpen(false);
        toast.error(err instanceof ApiError ? err.message : 'Could not grant the bounty.');
      },
    });
  }

  function handleStatusChange(status: BugReportStatus): void {
    if (!reference) return;
    updateStatus.mutate(
      { reference, status },
      {
        onSuccess: () => toast.success(`Status updated to ${status}.`),
        onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not update status.'),
      }
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={report.reference} description="Bug report detail (PRD-0013 R3)." />

      <SectionCard title="Reporter">
        <Field label="Email">{report.reporterEmail ?? <Badge variant="outline">anonymous</Badge>}</Field>
        <Field label="User id">{report.userId ?? '—'}</Field>
      </SectionCard>

      <SectionCard title="Report">
        <Field label="Category">{report.category ?? <Badge variant="outline">pre-R1</Badge>}</Field>
        {report.structuredAnswers &&
          Object.entries(report.structuredAnswers).map(([q, a]) => (
            <Field key={q} label={structuredAnswerLabel(report.category, q)}>
              {a}
            </Field>
          ))}
        <p className="mt-2 whitespace-pre-wrap text-sm">{report.body}</p>
      </SectionCard>

      <SectionCard title="Diagnostics">
        {report.diagnostics && Object.keys(report.diagnostics).length > 0 ? (
          Object.entries(report.diagnostics).map(([key, value]) => (
            <Field key={key} label={diagnosticLabel(key)}>
              {formatDiagnosticValue(value)}
            </Field>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">No diagnostics reported.</p>
        )}
      </SectionCard>

      <SectionCard title="Status">
        <div className="flex gap-2">
          {STATUS_OPTIONS.map((status) => (
            <Button
              key={status}
              size="sm"
              variant={report.status === status ? 'default' : 'outline'}
              // A granted ticket is final — the backend 409s any further status change on
              // it — and re-clicking the already-selected status would only fire a no-op PUT.
              disabled={updateStatus.isPending || report.status === 'granted' || report.status === status}
              onClick={() => handleStatusChange(status)}
            >
              {status}
            </Button>
          ))}
        </div>
        {report.status !== 'granted' && (
          <Button
            className="mt-3"
            size="sm"
            disabled={confirmGrant.isPending || report.userId === null || campaign.data?.canGrant === false}
            onClick={() => setConfirmGrantOpen(true)}
          >
            Confirm &amp; grant
          </Button>
        )}
      </SectionCard>

      <SectionCard title="Sentry / PostHog correlation">
        {report.sentryUrl || report.postHogUrl ? (
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted-foreground">
              Best-effort — matched by device id and a time window, not an exact link.
            </span>
            {report.sentryUrl && <a href={report.sentryUrl} className="underline">Sentry issues near this time</a>}
            {report.postHogUrl && <a href={report.postHogUrl} className="underline">PostHog session replays (search by device id)</a>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No Sentry or PostHog correlation found.</p>
        )}
      </SectionCard>

      <SectionCard title="Attachments">
        {report.attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No attachments.</p>
        ) : (
          <AttachmentPreview attachments={report.attachments} />
        )}
      </SectionCard>

      <SectionCard title="Conversation">
        <ConversationThread reference={report.reference} messages={report.messages} />
      </SectionCard>

      <ConfirmDialog
        open={confirmGrantOpen}
        onOpenChange={setConfirmGrantOpen}
        title="Confirm this bug report?"
        description={`User ${report.userId ?? '—'} receives ${campaign.data?.rewardMonths ?? 12} months of Premium. This cannot be undone from here.`}
        confirmLabel="Confirm & grant"
        pending={confirmGrant.isPending}
        onConfirm={handleGrant}
      />
    </div>
  );
}
